import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, ExternalLink, Quote,
  Bot, CheckCircle, Coffee, QrCode, Building2, Timer, Handshake,
  BookOpen, ChevronRight, XCircle
} from 'lucide-react';
import Logo from '../components/Logo';

export default function About() {
  const navigate = useNavigate();
  const [sectionsOpen, setSectionsOpen] = useState({ features: false });
  const toggleSection = (key) => setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));

  const features = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: 'Your NetworkBot Agent',
      description: 'NetworkBot runs daily — finds matches, drafts intros, and posts your pitch to Agent Rooms. Your agent is already signed up.',
      highlight: true,
      joinLink: '/networkbot',
      ctaLabel: 'Activate yours',
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Web Verified Profiles',
      description: 'Verify your professional presence and earn the ✓ Web Verified badge. Build trust before you even connect.',
      isNew: true,
    },
    {
      icon: <Coffee className="w-8 h-8" />,
      title: 'Brew Circles',
      description: 'Speak from where you are. Build trust first. Fewer words. More honesty.',
      helpLink: '/help/brew-circles',
      joinLink: '/brew-circles',
    },
    {
      icon: <QrCode className="w-8 h-8" />,
      title: 'Share your MU-Pin, not your card',
      description: 'Skip the small talk at events. Scan, match on intent, and know exactly why you should talk.',
      helpLink: '/help/mu-pin',
      pinLookupTrigger: true,
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Communities',
      description: 'Join your coworking space or business community to see members first in your feed and connect with your network.',
      helpLink: '/help/communities',
      joinLink: '/join-community',
    },
    {
      icon: <Timer className="w-8 h-8" />,
      title: 'Relationship Decay System',
      description: 'Connections fade without effort. Nurture what matters or watch it expire. Real relationships require real maintenance.',
      helpLink: '/help/relationship-decay',
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: 'Pay-It-Forward Pledges',
      description: 'Got help? Pledge to help one person back. Track your impact chain and see how your generosity ripples through the community.',
      comingSoon: true,
      helpLink: '/help/pay-it-forward',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Helmet>
        <title>About Match It Up© — Platform & Philosophy</title>
        <meta name="description" content="What is Match It Up? Why we built it, what it is not, and the features that make intentional professional networking possible." />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FFFDF7] border-b-[3px] border-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link
            to="/"
            state={{ fromLogo: true }}
            className="flex items-center gap-2 text-[#0F172A]/60 hover:text-[#0F172A] transition-colors font-mono text-sm font-black"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <Link to="/" state={{ fromLogo: true }}>
            <Logo size="sm" />
          </Link>
          <Link
            to="/register"
            className="bg-[#E8430A] text-white px-4 py-1.5 text-sm font-black border-2 border-black rounded-xl"
            style={{ boxShadow: '3px 3px 0 #000' }}
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Page Heading */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#FFFDF7] border-b-4 border-black">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-xs font-black tracking-widest text-[#E8430A] uppercase font-mono mb-3 block">
            Who we are
          </span>
          <h1
            className="font-black uppercase text-[#0F172A]"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              letterSpacing: '-0.04em',
              lineHeight: '1',
            }}
          >
            ABOUT MATCH IT UP™
          </h1>
          <p className="font-mono text-sm text-[#0F172A]/50 mt-3 max-w-xl mx-auto">
            The platform, the philosophy, and the features that make intentional professional networking possible.
          </p>
        </div>
      </section>

      {/* Feature Grid — WHY MATCH IT UP? */}
      <section id="why-match-it-up" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#FFFDF7] border-b-4 border-black">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="text-xs font-black tracking-widest text-[#E8430A] uppercase mb-3 block font-mono">
              Platform Utilities
            </span>
            <h2
              className="font-black uppercase text-[#0F172A] mb-3"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                letterSpacing: '-0.03em',
                lineHeight: '1',
              }}
            >
              WHY MATCH IT UP?
            </h2>
            <p className="font-mono text-sm text-[#0F172A]/50 italic">
              &ldquo;intentional professional networking — less noise, more signal&rdquo;
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const isDark     = index === 0;
              const isGold     = index === 2;
              const bgColor    = isDark ? '#0F172A' : isGold ? '#FEF9C3' : '#FFFFFF';
              const textCls    = isDark ? 'text-white' : 'text-[#0F172A]';
              const descCls    = isDark ? 'text-white/60' : isGold ? 'text-[#0F172A]/70' : 'text-[#0F172A]/60';
              const defShadow  = isDark ? '10px 10px 0 #E8430A' : '10px 10px 0 #0F172A';
              const hovShadow  = isDark ? '14px 14px 0 #FFE135' : '14px 14px 0 #E8430A';

              return (
                <React.Fragment key={index}>
                  {index === 3 && (
                    <div className="col-span-full">
                      <div
                        onClick={() => toggleSection('features')}
                        className="cursor-pointer flex items-center justify-between border border-slate-200 px-6 py-4 bg-[#FFFDF7] transition-all duration-150"
                        style={{ boxShadow: '3px 3px 0 #0F172A' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-3px,-3px)'; e.currentTarget.style.boxShadow = '9px 9px 0 #E8430A'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '6px 6px 0 #0F172A'; }}
                        data-testid="about-features-toggle"
                      >
                        <div>
                          <p className="font-black text-[#0F172A] uppercase tracking-tight">
                            + {features.length - 3} more features
                          </p>
                          <p className="font-mono text-sm text-[#0F172A]/50 mt-0.5">
                            MU-Pin, Communities, Relationship Decay &amp; Pay-it-Forward
                          </p>
                        </div>
                        <div
                          className={`w-8 h-8 border border-slate-200 flex items-center justify-center flex-shrink-0 ml-4 bg-white transition-transform duration-200 ${sectionsOpen.features ? 'rotate-45' : ''}`}
                        >
                          <span className="text-[#E8430A] text-xl font-black leading-none">+</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(index, 3) * 0.07 }}
                    viewport={{ once: true }}
                    className={`${index >= 3 && !sectionsOpen.features ? 'hidden' : ''}`}
                    data-testid={`about-feature-card-${index}`}
                  >
                    <div
                      className="h-full p-7 flex flex-col justify-start border border-slate-200 transition-all duration-150"
                      style={{ backgroundColor: bgColor, boxShadow: defShadow, minHeight: '250px' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-4px,-4px)'; e.currentTarget.style.boxShadow = hovShadow; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = defShadow; }}
                    >
                      <span className="font-mono text-xs font-bold mb-4 block text-[#E8430A]">
                        {String(index + 1).padStart(2, '0')}/
                      </span>
                      <div className={`w-10 h-10 mb-3 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                        {feature.icon}
                      </div>
                      <h3 className={`font-black uppercase text-xl mb-3 leading-tight tracking-tight ${textCls}`}>
                        {feature.title}
                        {feature.comingSoon && (
                          <span className="ml-2 text-[10px] bg-[#3A5068] text-white px-1.5 py-0.5 font-medium normal-case tracking-normal">
                            Coming Soon
                          </span>
                        )}
                        {feature.isNew && (
                          <span className="ml-2 text-[10px] bg-[#0A7A5E] text-white px-1.5 py-0.5 font-medium animate-pulse normal-case tracking-normal">
                            NEW
                          </span>
                        )}
                        {feature.highlight && feature.joinLink && (
                          <span className="ml-2 text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 font-medium normal-case tracking-normal">
                            LIVE
                          </span>
                        )}
                      </h3>
                      <p className={`font-mono text-xs leading-relaxed flex-1 ${descCls}`}>
                        {feature.description}
                      </p>
                      <div className="flex items-center gap-4 pt-4">
                        {feature.helpLink && (
                          <Link
                            to={feature.helpLink}
                            className={`inline-flex items-center gap-1 text-[10px] font-black font-mono uppercase tracking-wider transition-colors ${isDark ? 'text-white/80 hover:text-white' : 'text-[#E8430A] hover:text-[#d13a08]'}`}
                          >
                            Learn more <ExternalLink className="w-2.5 h-2.5" />
                          </Link>
                        )}
                        {feature.joinLink && (
                          <Link
                            to={feature.joinLink}
                            className={`inline-flex items-center gap-1 text-[10px] font-black font-mono uppercase tracking-wider transition-colors ${isDark ? 'text-[#FFE135] hover:text-white' : 'text-[#E8430A] hover:text-[#d13a08]'}`}
                          >
                            {feature.ctaLabel || 'Explore'} <ArrowRight className="w-2.5 h-2.5" />
                          </Link>
                        )}
                        {feature.pinLookupTrigger && (
                          <Link
                            to="/lookup"
                            className={`inline-flex items-center gap-1 text-[10px] font-black font-mono uppercase tracking-wider transition-colors ${isDark ? 'text-white/80 hover:text-white' : 'text-[#E8430A] hover:text-[#d13a08]'}`}
                          >
                            Have a MU-Pin? <ArrowRight className="w-2.5 h-2.5" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Founder's Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-10"
            data-testid="about-founders-note"
          >
            <div
              onClick={() => navigate('/founders-note')}
              className="border border-slate-200 p-6 sm:p-8 cursor-pointer group bg-white transition-all duration-150"
              style={{ boxShadow: '3px 3px 0 #0F172A' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-3px,-3px)'; e.currentTarget.style.boxShadow = '9px 9px 0 #E8430A'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '6px 6px 0 #0F172A'; }}
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 border border-slate-200 flex items-center justify-center bg-[#FEF1EC]">
                    <Quote className="w-8 h-8 text-[#E8430A]" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="font-mono text-lg text-[#0F172A] italic mb-1">
                    &ldquo;Some systems work. That doesn&apos;t mean they work forever.&rdquo;
                  </p>
                  <p className="font-mono text-sm text-[#0F172A]/50">
                    Read the story behind Match It Up©
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-2 text-[#E8430A] font-black uppercase text-sm tracking-wider group-hover:gap-3 transition-all">
                    Founder&apos;s Note <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resource Centre + What MIU is NOT */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#FFFDF7] border-b-4 border-black">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            onClick={() => navigate('/resources')}
            className="border border-slate-200 p-6 cursor-pointer bg-white flex items-center justify-between group transition-all duration-150"
            style={{ boxShadow: '4px 4px 0 #0F172A' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-3px,-3px)'; e.currentTarget.style.boxShadow = '11px 11px 0 #E8430A'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '8px 8px 0 #0F172A'; }}
            data-testid="about-resource-centre-link"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-[#0F172A] border border-slate-200 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-black uppercase text-lg text-[#0F172A] tracking-tight">Resource Centre</h3>
                <p className="font-mono text-xs text-[#0F172A]/60 mt-1">
                  Insights on networking, trust &amp; building meaningful connections
                </p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-[#E8430A] group-hover:translate-x-2 transition-transform flex-shrink-0" />
          </div>

          <div
            onClick={() => navigate('/what-match-it-up-is-not')}
            className="border border-slate-200 p-6 cursor-pointer bg-[#E6FFF5] flex items-center justify-between group transition-all duration-150"
            style={{ boxShadow: '4px 4px 0 #0F172A' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-3px,-3px)'; e.currentTarget.style.boxShadow = '11px 11px 0 #E8430A'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '8px 8px 0 #0F172A'; }}
            data-testid="about-miu-not-link"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-[#0A7A5E] border border-slate-200 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-black uppercase text-lg text-[#0F172A] tracking-tight">
                  What Match It Up© is <span className="text-[#E8430A]">NOT</span>
                </h3>
                <p className="font-mono text-xs text-[#0F172A]/60 mt-1">
                  Built for intentional networking. Less noise. More signal.
                </p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-[#E8430A] group-hover:translate-x-2 transition-transform flex-shrink-0" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm font-mono mb-4">
            Made with ❤️ in 🇮🇳 — Match It Up©
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-xs text-white/40 font-mono">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/resources" className="hover:text-white transition-colors">Resources</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
